import { PolymarketMarket } from '../detail/market/types';

type MarketPrice = string | number | null;

interface MarketResolvable {
  /** DB 行为字符串 '0'/'1'（polymarket_market_realtime），部分接口序列化为 number */
  market_closed?: number | string | null;
  // 宽为 string：DB 行（PolymarketMarketRow）未收窄为字面量联合，这里只做等值比较
  uma_resolution_status?: PolymarketMarket['uma_resolution_status'] | string | null;
  outcome_yes_price?: MarketPrice;
}

interface MarketOutcomeLike extends MarketResolvable {
  outcome_yes?: string | null;
  outcome_no?: string | null;
}

export function isMarketResolved(market?: MarketResolvable | null): boolean {
  if (!market) return false;
  if (market.outcome_yes_price) {
    return string2Number(market.outcome_yes_price) >= 0.995;
  }
  return !!market.market_closed || market.uma_resolution_status === 'resolved';
}

export function getMarketOutcome(market?: MarketOutcomeLike | null): string {
  if (!market) return '';

  if (market.uma_resolution_status === 'resolved' || market.market_closed) {
    const yes = market.outcome_yes?.toUpperCase() || 'YES';
    const no = market.outcome_no?.toUpperCase() || 'NO';
    return string2Number(market.outcome_yes_price) >= 0.5 ? yes : no;
  }

  if (market.uma_resolution_status) {
    return market.uma_resolution_status;
  }

  return market.outcome_yes_price == null ? '' : String(market.outcome_yes_price);
}

export function getMarketStatus(market?: MarketResolvable | null) {
  if (!market) return '';

  if (market.uma_resolution_status === 'resolved' || market.market_closed) {
    return 'Ended';
  }

  if (market.uma_resolution_status) {
    return market.uma_resolution_status;
  }

  return '';
}

function string2Number(value?: MarketPrice) {
  return Number(value) || 0;
}

interface MarketSortable {
  outcome_yes_price: MarketPrice;
  group_item_threshold?: MarketPrice;
}

export function sortMarketItems<T extends MarketSortable>(markets: T[], sortBy?: string): T[] {
  if (sortBy === 'price') {
    return markets
      .sort((a, b) => string2Number(b.outcome_yes_price) - string2Number(a.outcome_yes_price))
      .slice(0, 20);
  }
  return markets
    .sort(
      (a, b) =>
        string2Number(a.group_item_threshold ?? '') - string2Number(b.group_item_threshold ?? ''),
    )
    .slice(0, 20);
}

export function findDisplayMarket<T extends MarketResolvable & MarketSortable>(
  markets: T[],
  eventClosed?: boolean,
): T | null {
  if (!markets || markets.length === 0) return null;

  if (eventClosed) {
    return markets[0];
  }

  const unresolvedMarket = markets.filter((m) => !isMarketResolved(m));
  const sortedMarkets = sortMarketItems(unresolvedMarket, 'price');

  return sortedMarkets[0] ?? null;
}

/** 结构化参数：兼容 EventData（详情页）与 MarketListEvent（列表，tags 可缺省）等任意带 tags 的事件 */
export function isWorldCupMarket(event: {
  tags?: { slug: string }[] | null;
  title: string;
}): boolean {
  return (
    !!event.tags?.some((tag) => tag.slug === 'fifa-world-cup') && event.title.includes(' vs. ')
  );
}
